import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { request } from "../../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import { makeError } from "../../../utils";
import * as Yup from "yup";
import NotFoundTitle from "../utilComponent/NotFoundTitle";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const AddPackage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataFound, setDataFound] = useState(true);

  const [services, setServices] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  const [formData, setFormData] = useState({
    service_id: "",
    title: "",
    price: "",
    description: "",
  });

  const schema = Yup.object().shape({
    service_id: Yup.string().required("Service is required"),
    title: Yup.string().required("Package title is required"),
    price: Yup.number()
      .typeError("Price must be a number")
      .required("Price is required"),
    description: Yup.string().test(
      "is-empty",
      "Package description is required",
      (value) =>
        value && value.replace(/<(.|\n)*?>/g, "").trim().length > 0
    ),
  });

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);

    try {
      await schema.validateAt(name, updated);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [name]: error.message }));
    }
  };

  const getServices = async () => {
    const response = await request({ url: "services", method: "GET" });
    return response.data?.items || [];
  };

  const getPackageById = async () => {
    try {
      setLoading(true);
      const res = await request({
        url: `packages/${id}`,
        method: "GET",
      });

      if (res?.data) {
        setFormData({
          service_id: res.data.service_id || "",
          title: res.data.title || "",
          price: res.data.price || "",
          description: res.data.description || "",
        });
        setDataFound(true);
      } else {
        setDataFound(false);
      }
    } catch (error) {
      setDataFound(false);
      makeError(error, toast);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingDropdowns(true);
        const servicesData = await getServices();
        setServices(servicesData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingDropdowns(false);
      }
    };

    fetchData();
    if (id) getPackageById();
  }, [id]);

  const submit = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      id ? updatePackage() : addPackage();
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((e) => {
        newErrors[e.path] = e.message;
      });
      setErrors(newErrors);
    }
  };

  const getPayload = () => {
    const payload = new FormData();
    payload.append("service_id", formData.service_id);
    payload.append("title", formData.title);
    payload.append("price", formData.price);
    payload.append("description", formData.description);
    return payload;
  };

  const addPackage = async () => {
    try {
      setPostLoading(true);
      await request({
        url: "packages",
        method: "POST",
        data: getPayload(),
      });
      toast.success("Package added successfully");
      navigate("/packages-list");
    } catch (error) {
      makeError(error, toast);
    } finally {
      setPostLoading(false);
    }
  };

  const updatePackage = async () => {
    try {
      setPostLoading(true);
      await request({
        url: `packages/${id}`,
        method: "POST",
        data: getPayload(),
      });
      toast.success("Package updated successfully");
      navigate("/packages-list");
    } catch (error) {
      makeError(error, toast);
    } finally {
      setPostLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  return (
    <>
      <ToastContainer />

      <div className="container-fluid px-4 py-3">
        <ol className="breadcrumb bg-transparent mb-3">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item">Packages</li>
          <li className="breadcrumb-item active">
            {id ? "Update" : "Add"} Package
          </li>
        </ol>

        {loading ? (
          <div className="text-center">Loading...</div>
        ) : !dataFound ? (
          <NotFoundTitle message="Package not found" />
        ) : (
          <div className="card">
            <div className="card-header">
              <h6>{id ? "Update Package" : "Add New Package"}</h6>
            </div>

            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Service *</label>
                  <select
                    name="service_id"
                    className="form-select"
                    value={formData.service_id}
                    onChange={handleInputChange}
                    disabled={loadingDropdowns}
                  >
                    <option value="">Select Service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.title}
                      </option>
                    ))}
                  </select>
                  {errors.service_id && (
                    <div className="text-danger">{errors.service_id}</div>
                  )}
                </div>

                {/* TITLE */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Package Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    className="form-control"
                    value={formData.title}
                    onChange={handleInputChange}
                  />
                  {errors.title && (
                    <div className="text-danger">{errors.title}</div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Price *</label>
                  <input
                    type="number"
                    name="price"
                    className="form-control"
                    value={formData.price}
                    onChange={handleInputChange}
                  />
                  {errors.price && (
                    <div className="text-danger">{errors.price}</div>
                  )}
                </div>

                {/* DESCRIPTION */}
                <div className="col-12">
                  <label className="form-label fw-semibold">
                    Package Description *
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: value,
                      }))
                    }
                    modules={quillModules}
                  />
                  {errors.description && (
                    <div className="text-danger mt-1">
                      {errors.description}
                    </div>
                  )}
                </div>

              </div>
            </div>

            <div className="card-footer text-end">
              <button
                className="btn btn-secondary"
                onClick={() => navigate("/packages-list")}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary ms-3"
                onClick={submit}
                disabled={postLoading}
              >
                {postLoading
                  ? "Saving..."
                  : id
                  ? "Update Package"
                  : "Add Package"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AddPackage;
